import { ApiError, type ProblemDetails } from '@/lib/apiError';

/**
 * `fetch` has no way to report upload progress - only response/download progress via a
 * `ReadableStream`, which is no help for the bytes going up. `XMLHttpRequest` is the only
 * browser primitive that exposes it (`xhr.upload.onprogress`), so this wraps it in the same
 * `Promise`-based shape `lib/api.ts`'s `api()` already gives the rest of the app. `api()` sends
 * every FormData body through here, so each upload in the app gets the stall watchdog below;
 * a caller that wants the progress itself (the lookbook Pages card) calls this directly.
 */
export interface UploadOptions {
    /** Called with the fraction uploaded so far, 0 to 1. Only fires when the browser knows the
     * total size, which it always does for a `FormData` body. */
    onProgress?: (fraction: number) => void;
    /** Aborts the request; the returned promise then rejects with a `DOMException` named
     * `AbortError`, the same shape `fetch` itself rejects with on an aborted `AbortSignal`. */
    signal?: AbortSignal;
}

/**
 * How long an upload may go without a byte leaving before it is given up on. The case this is
 * for is a refusal at the edge: CloudFront answers a request its WAF blocks before the body is
 * in and stops reading, and once the body is more than a few hundred kilobytes Chromium never
 * hands that answer to the page, by XHR or by fetch (ALS-332, measured against staging). The
 * bytes just stop, and the screen would say "Uploading…" until the person gave up. Progress
 * events come every 50ms while anything is moving at all, so a silence this long is never a
 * slow line.
 */
const STALL_MS = 30_000;

/**
 * How long the answer may take once the whole body is up. CloudFront answers 504 itself when
 * the API has been silent for 60 seconds (origin_read_timeout in the admin_portal module's
 * cloudfront.tf), so this only fires on a connection that has died.
 */
const REPLY_MS = 90_000;

export const STALLED = 'The upload stopped getting through and was cancelled. Try again.';

export function uploadWithProgress<T = void>(
    method: string,
    path: string,
    body: FormData,
    options: UploadOptions = {},
): Promise<T> {
    const { onProgress, signal } = options;

    return new Promise<T>((resolve, reject) => {
        if (signal?.aborted) {
            reject(new DOMException('The upload was aborted.', 'AbortError'));
            return;
        }

        const xhr = new XMLHttpRequest();
        xhr.open(method, path, true);
        xhr.withCredentials = true;
        xhr.setRequestHeader('Accept', 'application/json');

        // Re-armed by every sign of life; when it fires, the abort below is ours, not the caller's.
        let watchdog: ReturnType<typeof setTimeout> | undefined;
        let stalled = false;
        function arm(ms: number) {
            clearTimeout(watchdog);
            watchdog = setTimeout(() => {
                stalled = true;
                xhr.abort();
            }, ms);
        }

        xhr.upload.onprogress = (event) => {
            arm(STALL_MS);
            if (onProgress && event.lengthComputable) onProgress(event.loaded / event.total);
        };
        xhr.upload.onload = () => arm(REPLY_MS);

        function onAbort() {
            xhr.abort();
        }
        signal?.addEventListener('abort', onAbort);

        xhr.onabort = () =>
            reject(
                stalled
                    ? new ApiError(0, { title: STALLED })
                    : new DOMException('The upload was aborted.', 'AbortError'),
            );
        xhr.onerror = () =>
            reject(new ApiError(0, { title: 'The upload could not be completed.' }));
        xhr.onloadend = () => {
            clearTimeout(watchdog);
            signal?.removeEventListener('abort', onAbort);
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                // Unlike a `.then()` callback or an async function's own body, a thrown error
                // inside a raw event handler is not funnelled into this promise by anything -
                // it would just become an uncaught exception, and the promise would never
                // settle, leaving a caller (LookbookPagesShell, say) stuck on "Uploading…"
                // forever with only Cancel to get out of it. So this catches explicitly, the
                // same way parseProblem below already does for the failure path.
                try {
                    resolve((xhr.responseText ? JSON.parse(xhr.responseText) : undefined) as T);
                } catch {
                    reject(
                        new ApiError(xhr.status, {
                            title: 'The upload finished, but the reply could not be read.',
                        }),
                    );
                }
                return;
            }
            reject(new ApiError(xhr.status, parseProblem(xhr)));
        };

        arm(STALL_MS);
        xhr.send(body);
    });
}

function parseProblem(xhr: XMLHttpRequest): ProblemDetails {
    try {
        const parsed: unknown = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
        if (parsed && typeof parsed === 'object') return parsed;
    } catch {
        // not JSON: fall through to the status line, the same way lib/api.ts's own readProblem does
    }
    return { status: xhr.status, title: xhr.statusText || undefined };
}
